import axiosInstance from "@/config/axiosInstance";
import { ActionApi } from "@/config/cms/api";
import { getCMSApiRoute } from "@/helpers/api.util";
import BookmarkEmptyIcon from "@/helpers/svg/bookmarkEmpty.svg";
import SerialNameIcon from "@/helpers/svg/serialNameIcon.svg";
import ThumbsDown from "@/helpers/svg/thumbsDown.svg";
import ThumbsDownFilled from "@/helpers/svg/thumbsDownFilled.svg";
import ThumbsUp from "@/helpers/svg/thumbsUp.svg";
import ThumbsUpFilled from "@/helpers/svg/thumbsUpFilled.svg";
import { Fade } from "@mui/material";
import { Button } from "@nextui-org/react";
import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

const ContentActions = ({ contentKey }) => {
  const { profile } = useSelector((state) => state.Auth);
  const [actionsLoading, setActionsLoading] = useState({
    bookmark: false,
    like: false,
    dislike: false,
    master: false,
  });
  const [actionData, setActionData] = useState({});

  const getContentActions = async () => {
    setActionsLoading(Object.assign({}, actionsLoading, { master: true }));
    try {
      const response = await axiosInstance.get(
        getCMSApiRoute("action/content"),
        {
          params: {
            contentKey,
          },
        }
      );
      if (response.status === 200) {
        const { data } = response.data;
        setActionData(data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setActionsLoading(Object.assign({}, actionsLoading, { master: false }));
    }
  };

  useEffect(() => {
    getContentActions();

    // Refetch when returning from player
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        getContentActions();
      }
    };
    const handlePageShow = (e) => {
      if (e.persisted) getContentActions();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pageshow", handlePageShow);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  const handleSaveBookmark = async () => {
    setActionsLoading(Object.assign({}, actionsLoading, { bookmark: true }));

    try {
      let response;
      if (!actionData?.userActions?.bookmark) {
        response = await ActionApi.prototype.actionControllerInsertBookmark(
          {
            contentKey,
          },
          {
            headers: {
              Authorization: `Bearer ${Cookies.get("userAccessToken")}`,
            },
          }
        );
        setActionData({
          ...actionData,
          userActions: {
            ...actionData.userActions,
            bookmark: true,
          },
        });
      } else {
        response = await ActionApi.prototype.actionControllerRemoveBookmark(
          {
            contentKey,
          },
          {
            headers: {
              Authorization: `Bearer ${Cookies.get("userAccessToken")}`,
            },
          }
        );

        setActionData({
          ...actionData,
          userActions: {
            ...actionData.userActions,
            bookmark: false,
          },
        });
      }
    } catch (err) {
      console.log(err);
    } finally {
      setActionsLoading(Object.assign({}, actionsLoading, { bookmark: false }));
    }
  };

  const handleLike = async ({ action }) => {
    if (actionsLoading.like || actionsLoading.dislike) return;
    if (
      action === "like" &&
      actionData?.userActions?.likeOrDislike?.[0]?.likeOrDislike === 1
    )
      return;
    if (
      action === "dislike" &&
      actionData?.userActions?.likeOrDislike?.[0]?.likeOrDislike === 2
    )
      return;

    setActionsLoading(
      Object.assign(
        {},
        actionsLoading,
        action === "like" ? { like: true } : { dislike: true }
      )
    );
    try {
      let response;
      if (action === "like") {
        response = await ActionApi.prototype.actionControllerInsertLike(
          {
            contentKey,
          },
          {
            headers: {
              Authorization: `Bearer ${Cookies.get("userAccessToken")}`,
            },
          }
        );
        if (response.status === 201) {
          const { data } = response.data;

          if (data.actionType && data.actionType === 1) {
            setActionData({
              ...actionData,
              userActions: {
                ...actionData?.userActions,
                likeOrDislike: [
                  {
                    ...actionData?.userActions?.likeOrDislike?.[0],
                    likeOrDislike: 1,
                  },
                ],
              },
            });
          }
        }
      } else if (action === "dislike") {
        response = await ActionApi.prototype.actionControllerInsertDislike(
          {
            contentKey,
          },
          {
            headers: {
              Authorization: `Bearer ${Cookies.get("userAccessToken")}`,
            },
          }
        );

        if (response.status === 201) {
          const { data } = response.data;
          if (data.actionType && data.actionType === 2) {
            setActionData({
              ...actionData,
              userActions: {
                ...actionData?.userActions,
                likeOrDislike: [
                  {
                    ...actionData?.userActions?.likeOrDislike?.[0],
                    likeOrDislike: 2,
                  },
                ],
              },
            });
          }
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setActionsLoading(
        Object.assign(
          {},
          actionsLoading,
          action === "like" ? { like: false } : { dislike: false }
        )
      );
    }
  };

  if (!profile.isLogin) return null;

  return (
    <Fade in={profile.isLogin && !actionsLoading.master} timeout={300}>
      <div className="content-detail-content-actions flex gap-3 pl-4 z-10 py-2 md:py-0">
        <Button
          isIconOnly
          variant="light"
          onClick={() =>
            handleLike({
              action: "dislike",
            })
          }
          isLoading={actionsLoading.dislike}
        >
          {actionData.userActions?.likeOrDislike[0]?.likeOrDislike === 2 ? (
            <ThumbsDownFilled />
          ) : (
            <ThumbsDown />
          )}
        </Button>
        <Button
          isIconOnly
          isLoading={actionsLoading.like}
          variant="light"
          onClick={() =>
            handleLike({
              action: "like",
            })
          }
        >
          {actionData.userActions?.likeOrDislike[0]?.likeOrDislike === 1 ? (
            <ThumbsUpFilled />
          ) : (
            <ThumbsUp />
          )}
        </Button>
        <Button
          variant="light"
          isLoading={actionsLoading.bookmark}
          isIconOnly
          onClick={handleSaveBookmark}
        >
          {actionData?.userActions?.bookmark ? (
            <SerialNameIcon />
          ) : (
            <BookmarkEmptyIcon />
          )}
        </Button>
      </div>
    </Fade>
  );
};

export default ContentActions;
